// @ts-check
import _keyBy from 'lodash/keyBy';
import { DEFAULT_COUNTRY_SELECTED } from 'constants/index';
import { convertAurusCardType, maskCardNumber } from './cardTypeUtils';

/**
 * @typedef {import('../types').UCAAddressDTO} UCAAddressDTO
 * @typedef {import('../types').UCAPaymentDTO} UCAPaymentDTO
 * @typedef {import('../types').CFAAddressDTO} CFAAddressDTO
 * @typedef {import('../types').CFAPaymentDTO} CFAPaymentDTO
 */

/**
 * @param {UCAAddressDTO} param0
 * @returns {CFAAddressDTO}
 */
export const mapUcaAddressToCfa = ({
  postalCode,
  firstName,
  lastName,
  country,
  state,
  city,
  address1,
  address2,
  phoneNumber,
  defaultAddress,
  addressId,
  addressType,
}) => ({
  firstName,
  lastName,
  state,
  city,
  defaultAddress,
  addressId,
  addressType,
  country: country || '',
  zip: postalCode || '',
  addressLine1: address1 || '',
  addressLine2: address2 || '',
  // Added due to inconsistency across the app
  line1: address1,
  line2: address2,
  dayTelephone: phoneNumber || '',
});

/**
 *
 * @param {CFAAddressDTO} param0
 * @returns {UCAAddressDTO}
 */
export const mapCfaAddressToUca = ({
  firstName,
  lastName,
  country,
  state,
  city,
  zip,
  phone,
  dayTelephone,
  line1,
  line2,
  defaultAddress,
  addressId,
  addressType,
}) => ({
  firstName,
  lastName,
  state,
  city,
  defaultAddress,
  addressType,
  addressId,
  country: country || '',
  postalCode: zip,
  address1: line1,
  address2: line2 || '',
  phoneNumber: phone || dayTelephone,
  phoneType: 'M',
});

/**
 * @param {UCAAddressDTO[]} addresses
 */
export const splitAndMapAddressesByType = (addresses, isBGAssociate, isBGFarfetchEnabled) => {
  /** @type {CFAAddressDTO[]} */
  const shippingAddress = [];
  /** @type {CFAAddressDTO[]} */
  const billingAddress = [];

  addresses.forEach((address) => {
    if (address.addressType === 'billing' || (isBGAssociate && isBGFarfetchEnabled && address.addressType === 'any')) {
      billingAddress.push(mapUcaAddressToCfa(address));
    }
    if (address.addressType === 'shipping' || (isBGAssociate && isBGFarfetchEnabled && address.addressType === 'any')) {
      shippingAddress.push(mapUcaAddressToCfa(address));
    }
  });

  return { billingAddress, shippingAddress };
};

/**
 * @param {{[x: string]: CFAAddressDTO}} billingAddressMap
 */
const mapUcaPaymentToCfa =
  (billingAddressMap) =>
  ({ expMonth, expYear, defaultPayment, cardIdentifier, maskedCardNumber, cardType, billingAddressId, paymentId }) => ({
    paymentId,
    billingAddress: billingAddressMap[billingAddressId],
    cardExpMonth: expMonth,
    cardExpYear: expYear,
    defaultCard: defaultPayment,
    keyIdentifier: cardIdentifier,
    maskedNumber: maskedCardNumber,
    networkType: cardType,
  });

export const mapUcaProfileAttributesToCfa = ({ email, firstName, lastName, webProfileId, ucaId }) => ({
  emailAddress: email,
  firstName,
  lastName,
  profileId: webProfileId,
  clientId: ucaId || webProfileId,
});

/**
 * @param {UCAPaymentDTO[]} ucaPayments
 * @param {CFAAddressDTO[]} billingAddresses
 * @returns {CFAPaymentDTO[]}
 */
export const mapUcaPaymentsToCfa = (ucaPayments, billingAddresses = [], mapper = mapUcaPaymentToCfa) => {
  const billingAddressDictionary = _keyBy(billingAddresses, (address) => address.addressId);
  return ucaPayments.map(mapper(billingAddressDictionary));
};

/**
 * @param {string} clientId
 * @param {CFAPaymentDTO} payment
 * @param {boolean} isUpdate
 * @returns {UCAPaymentDTO | Partial<UCAPaymentDTO>}
 */
export const mapCfaPaymentToUca = (clientId, payment, isUpdate = false) => {
  const billingAddress = {
    lastName: payment.billingAddress.lastName,
    firstName: payment.billingAddress.firstName,
    country: payment.billingAddress.country,
    city: payment.billingAddress.city,
    postalCode: payment.billingAddress.zip,
    state: payment.billingAddress.state,
    addressType: 'billing',
    phoneType: 'M',
    phoneNumber: payment.billingAddress.phone,
    address1: payment.billingAddress.line1,
    address2: payment.billingAddress.line2 || '',
    defaultAddress: payment.billingAddress.defaultAddress || false,
  };

  const existingPaymentIdentifiers = {
    billingAddressId: payment.billingAddress.addressId,
    paymentId: payment.paymentId,
  };

  const ucaPaymentPayload = {
    // client info
    accountId: clientId,
    // card info
    defaultPayment: payment.defaultCard, // defaultCard
    maskedCardNumber: maskCardNumber(payment.maskedNumber),
    cardType: convertAurusCardType(payment.networkType),
    expMonth: payment.cardExpMonth,
    expYear: payment.cardExpYear,
    cardIdentifier: payment.keyIdentifier, // keyIdentifier
    kiCardNumber: payment.keyIdentifier, // keyIdentifier
  };

  return Object.assign(ucaPaymentPayload, isUpdate ? existingPaymentIdentifiers : billingAddress);
};

export const preparePaymentsForUpdate = ({
  payments = [],
  selectedPayment,
  zeroDollarResponseData = {},
  isClientInformationFromUcaEnabled,
}) => {
  let updatedSelectedPayment = {};
  const uniqCardIdentifierKey = isClientInformationFromUcaEnabled ? 'paymentId' : 'maskedNumber';

  const foundPayment = payments.find(
    (payment) => selectedPayment && payment[uniqCardIdentifierKey] === selectedPayment[uniqCardIdentifierKey],
  );
  const updatedPayments = payments.map((payment) =>
    foundPayment && payment[uniqCardIdentifierKey] === foundPayment[uniqCardIdentifierKey]
      ? selectedPayment
      : {
          ...payment,
          defaultCard: selectedPayment?.defaultCard ? false : payment.defaultCard,
          selected: selectedPayment?.selected ? false : payment.selected,
        },
  );

  if (!foundPayment) {
    updatedSelectedPayment = {
      ...selectedPayment,
      ...zeroDollarResponseData,
    };
  }

  return { foundPayment, updatedPayments, updatedSelectedPayment };
};

export const isUSA = (countryCode) =>
  countryCode === DEFAULT_COUNTRY_SELECTED.value || countryCode === DEFAULT_COUNTRY_SELECTED.name;
